import React from 'react';
import { canAccessQCModule } from '../../utils/roleConstants';
import { Button, FormControl } from 'react-bootstrap';
import config from '../../../config';
import DataTable from 'react-data-table-component';
import { Navigate, useNavigate } from 'react-router';
import DataTableEllipsisText from '../../_components/DataTableEllipsisText';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import QValuePageHeader from '../../_components/QValuePageHeader';
import { Q_VALUE_PAGE_HEADERS } from '../../utils/qValuePageHeaders';
import { getQValueAccountId } from './qValueAccount';
import { CAR_STATUS_STYLES, formatDateOnly } from './carReportUtils';
import { exportCarListToExcel } from './carReportExport';

const summaryRibbonStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px 40px',
    background: '#e8f4fc',
    border: '1px solid #b8daff',
    borderRadius: '4px',
    padding: '14px 20px',
    marginBottom: '16px',
    fontWeight: 600,
};

const summaryItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '90px',
};

const tableCustomStyles = {
    headRow: {
        style: {
            backgroundColor: '#1e3a5f',
            color: '#fff',
            fontWeight: 700,
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            color: '#fff',
            fontSize: '13px',
        },
    },
};

function StatusCell({ status }) {
    const style = CAR_STATUS_STYLES[status] || CAR_STATUS_STYLES.Open;
    return (
        <span style={{
            ...style,
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: '13px',
        }}
        >
            {status}
        </span>
    );
}

class CorrectiveActionReportList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            keyword: '',
            list: [],
            summary: { total: 0, open: 0, inProgress: 0, closed: 0, overdue: 0 },
            currentPage: 1,
            rowsPerPage: 10,
            totalRows: 0,
            loading: false,
            exporting: false,
        };
        this.tableRequestSeq = 0;
    }

    componentDidMount() {
        this.buildColumns();
        this.loadTable();
    }

    buildColumns() {
        this.columns = [
            {
                name: 'CAR NO',
                selector: (row) => row.carNo,
                width: '150px',
                cell: (row) => (
                    <button
                        type="button"
                        className="btn btn-link p-0 qvalue-list-document-no"
                        onClick={() => this.openRecord(row.id)}
                    >
                        {row.carNo}
                    </button>
                ),
            },
            {
                name: 'Origin / Source',
                selector: (row) => row.originSource,
                width: '150px',
                cell: (row) => <DataTableEllipsisText value={row.originSource} />,
            },
            {
                name: 'Date Opened',
                selector: (row) => row.dateOpened,
                width: '120px',
            },
            {
                name: 'Problem Description',
                selector: (row) => row.problemDescription,
                grow: 2,
                cell: (row) => <DataTableEllipsisText value={row.problemDescription} />,
            },
            {
                name: 'Assigned Owner',
                selector: (row) => row.ownerName,
                width: '140px',
            },
            {
                name: 'Target Date',
                selector: (row) => row.targetDate,
                width: '120px',
            },
            {
                name: 'Date Closed',
                selector: (row) => row.dateClosed,
                width: '120px',
                cell: (row) => (row.dateClosed || '—'),
            },
            {
                name: 'Status',
                selector: (row) => row.displayStatus,
                width: '130px',
                cell: (row) => <StatusCell status={row.displayStatus} />,
            },
        ];
    }

    openRecord(id) {
        const path = `/corrective_action_report/${id}`;
        if (this.props.navigate) {
            this.props.navigate(path);
        } else {
            window.location.hash = `#${path}`;
        }
    }

    loadTable(page = null, limit = null) {
        const company = getQValueAccountId();
        if (!company) return;
        const currentPage = page != null ? page : this.state.currentPage;
        const rowsPerPage = limit != null ? limit : this.state.rowsPerPage;
        const offset = (currentPage - 1) * rowsPerPage;
        const requestSeq = ++this.tableRequestSeq;
        this.setState({ loading: true });
        fetch(`${config.apiUrl}/getCorrectiveActionReportListByCompany?company=${company}&keyword=${encodeURIComponent(this.state.keyword || '')}&limit=${rowsPerPage}&offset=${offset}`)
            .then((r) => r.json())
            .then((body) => {
                if (requestSeq !== this.tableRequestSeq) return;
                const rows = (body && body.data) || [];
                const list = rows.map((data) => ({
                    id: data.id,
                    carNo: data.carNo,
                    originSource: data.originSource,
                    dateOpened: formatDateOnly(data.dateOpened),
                    problemDescription: data.problemDescription,
                    ownerName: data.ownerName,
                    targetDate: formatDateOnly(data.targetDate),
                    dateClosed: formatDateOnly(data.dateClosed),
                    displayStatus: data.displayStatus,
                }));
                this.setState({
                    list,
                    summary: (body.meta && body.meta.summary) || this.state.summary,
                    totalRows: body.meta && body.meta.total_count ? body.meta.total_count : 0,
                    currentPage,
                    rowsPerPage,
                    loading: false,
                });
            })
            .catch(() => {
                if (requestSeq === this.tableRequestSeq) {
                    this.setState({ list: [], loading: false });
                }
            });
    }

    handlePageChange = (page) => {
        this.setState({ currentPage: page }, () => this.loadTable(page));
    };

    handleRowsPerPageChange = (newRowsPerPage, page) => {
        this.setState({ rowsPerPage: newRowsPerPage, currentPage: page }, () => this.loadTable(page, newRowsPerPage));
    };

    exportList = () => {
        const company = getQValueAccountId();
        if (!company) return;
        this.setState({ exporting: true });
        fetch(`${config.apiUrl}/getCorrectiveActionReportListByCompany?company=${company}&keyword=${encodeURIComponent(this.state.keyword || '')}&limit=10000&offset=0`)
            .then((r) => r.json())
            .then((body) => {
                const rows = (body && body.data) || [];
                const mapped = rows.map((data) => ({
                    carNo: data.carNo,
                    originSource: data.originSource,
                    dateOpened: formatDateOnly(data.dateOpened),
                    problemDescription: data.problemDescription,
                    ownerName: data.ownerName,
                    targetDate: formatDateOnly(data.targetDate),
                    dateClosed: formatDateOnly(data.dateClosed),
                    displayStatus: data.displayStatus,
                }));
                return exportCarListToExcel(mapped, body.meta && body.meta.summary);
            })
            .finally(() => this.setState({ exporting: false }));
    };

    renderSummary() {
        const s = this.state.summary;
        const items = [
            { label: 'TOTAL CARs', value: s.total },
            { label: 'OPEN', value: s.open },
            { label: 'IN PROGRESS', value: s.inProgress },
            { label: 'CLOSED', value: s.closed },
            { label: 'OVERDUE', value: s.overdue },
        ];
        return (
            <div style={summaryRibbonStyle}>
                {items.map(({ label, value }) => (
                    <div key={label} style={summaryItemStyle}>
                        <span style={{ fontSize: '12px', color: '#495057', marginBottom: 4 }}>{label}</span>
                        <span style={{ fontSize: '22px', color: '#0d6efd' }}>{value}</span>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!canAccessQCModule(user)) {
            return <Navigate to="/" />;
        }
        const { keyword, list, exporting } = this.state;
        const goNew = () => {
            const path = '/corrective_action_report/new';
            if (this.props.navigate) this.props.navigate(path);
            else window.location.hash = `#${path}`;
        };

        return (
            <div className="container">
                <QValuePageHeader {...Q_VALUE_PAGE_HEADERS.corrective_action_report} />
                {this.renderSummary()}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                    <FormControl type="text" placeholder="Search..." value={keyword || ''} onChange={(ev) => this.setState({ keyword: ev.target.value })} style={{ minWidth: 200 }} />
                    <Button variant="primary" onClick={() => this.setState({ currentPage: 1 }, () => this.loadTable(1))}>Search</Button>
                    <Button variant="outline-secondary" disabled={exporting} onClick={this.exportList}>
                        {exporting ? 'Exporting…' : 'Export Excel'}
                    </Button>
                    <Button variant="danger" style={{ marginLeft: 'auto' }} onClick={goNew}>New CAR</Button>
                </div>
                <DataTable
                    columns={this.columns}
                    data={list}
                    customStyles={tableCustomStyles}
                    progressPending={this.state.loading}
                    progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading CAR records" />}
                    pagination
                    paginationServer
                    paginationTotalRows={this.state.totalRows}
                    paginationPerPage={this.state.rowsPerPage}
                    paginationDefaultPage={this.state.currentPage}
                    onChangePage={this.handlePageChange}
                    onChangeRowsPerPage={this.handleRowsPerPageChange}
                    highlightOnHover
                    onRowClicked={(row) => this.openRecord(row.id)}
                    pointerOnHover
                />
            </div>
        );
    }
}

function withNavigate(Component) {
    return (props) => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
}

const CorrectiveActionReportListWithNav = withNavigate(CorrectiveActionReportList);
export { CorrectiveActionReportListWithNav as CorrectiveActionReportList };
